import type { PostHog } from 'posthog-js';

/**
 * PostHog product-analytics loader. posthog-js is dynamically imported so it stays out of
 * the main bundle and only downloads once the visitor grants analytics consent. We disable
 * posthog's own pageview capture and drive it from PublicAnalytics on each SPA route change
 * (consistent with GA). Withdrawing consent opts out + resets.
 */
let ph: PostHog | null = null;
let loading = false;

export async function loadPostHog(apiKey: string, host: string, proxyHost?: string | null): Promise<void> {
  if (typeof window === 'undefined' || ph || loading) return;
  loading = true;
  try {
    const posthog = (await import('posthog-js')).default;
    posthog.init(apiKey, {
      // Route ingestion through our own reverse proxy (e.g. ph.sieveguard.com) when the
      // operator configured one, so ad blockers can't drop events; otherwise hit PostHog
      // directly. ui_host stays the real PostHog host so in-app "open in PostHog" deep
      // links (toolbar, session-replay links) still resolve.
      api_host: proxyHost || host,
      ui_host: proxyHost ? host : undefined,
      capture_pageview: false, // SPA pageviews sent manually via posthogPageview()
      capture_pageleave: true,
      // Send Core Web Vitals (LCP / INP / CLS / FCP / TTFB) per route. Pairs with the
      // Cloudflare CWV report so we can also slice by tenant / feature flag / cohort
      // — which Cloudflare can't do. Sampling-aware on PostHog's side, so this is
      // safe even on high-volume routes.
      capture_performance: { web_vitals: true },
      // Autocapture unhandled errors + unhandled promise rejections into PostHog Error
      // Tracking, paired with the server-side capture in the API. React *render* errors
      // don't surface through window.onerror — the ErrorBoundary forwards those via
      // posthogCaptureException(). Only active post-consent (PostHog loads after the
      // analytics-cookie gate), same as the rest of our PostHog usage.
      capture_exceptions: true,
      // Native heatmaps — click / rageclick / deadclick / mousemove coordinate capture
      // that powers PostHog's Heatmaps tab. This is a lightweight coordinate batcher,
      // NOT session replay (no DOM snapshots), so it adds negligible memory/CPU. Enabled
      // so PostHog owns heatmaps too and we no longer need a second recorder (Microsoft
      // Clarity) whose heatmaps required full session recording — see PublicAnalytics.
      enable_heatmaps: true,
    });
    ph = posthog;
  } finally {
    loading = false;
  }
}

/** Send a manual SPA pageview (no-op until PostHog is loaded). */
export function posthogPageview(): void {
  ph?.capture('$pageview');
}

/**
 * Manually report an exception to PostHog Error Tracking. Used by the ErrorBoundary for
 * React render errors (which window.onerror autocapture misses). No-op until PostHog is
 * loaded (i.e. before analytics consent), matching the rest of our PostHog usage.
 */
export function posthogCaptureException(error: unknown, props?: Record<string, unknown>): void {
  ph?.captureException(error, props);
}

/** Stop capturing + clear identity on consent withdrawal. */
export function unloadPostHog(): void {
  if (!ph) return;
  ph.opt_out_capturing();
  ph.reset();
  ph = null;
}
